import { useState, useEffect } from "react";
import { Canal } from "@/entities/Canal";
import { Video } from "@/entities/Video";
import { Audio } from "@/entities/Audio";
import { Settings, Tv, Video as VideoIcon, Music, Plus, Pencil, Trash2, X, Save, RefreshCw, ChevronDown } from "lucide-react";

const TABS = [
  { id: "canales", label: "Canales TV", icon: Tv },
  { id: "videos", label: "Videos", icon: VideoIcon },
  { id: "audios", label: "Audios MP3", icon: Music },
];

const CANAL_CATS = ["Música", "Noticias", "Deportes", "Entretenimiento", "Variedad"];
const VIDEO_CATS = ["Música", "Conciertos", "Videoclips", "Entrevistas", "Variedad"];
const AUDIO_CATS = ["Salsa", "Cumbia", "Vallenato", "Pop", "Rock", "Reggaeton", "Tropical", "Variedad"];

const CANAL_VACIO = { nombre: "", url_stream: "", descripcion: "", categoria: "Música", imagen: "", activo: true, orden: 0 };
const VIDEO_VACIO = { titulo: "", url_video: "", descripcion: "", categoria: "Música", miniatura: "", artista: "", duracion: "", activo: true, orden: 0 };
const AUDIO_VACIO = { titulo: "", url_audio: "", artista: "", album: "", descripcion: "", categoria: "Variedad", portada: "", duracion: "", activo: true, orden: 0 };

export default function Administracion() {
  const [tab, setTab] = useState("canales");
  const [canales, setCanales] = useState([]);
  const [videos, setVideos] = useState([]);
  const [audios, setAudios] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [modal, setModal] = useState(null); // { tipo, datos, esNuevo }
  const [guardando, setGuardando] = useState(false);
  const [confirmEliminar, setConfirmEliminar] = useState(null);

  useEffect(() => { cargarTodo(); }, []);

  async function cargarTodo() {
    setCargando(true);
    const [c, v, a] = await Promise.all([
      Canal.list("orden", 200),
      Video.list("orden", 200),
      Audio.list("orden", 200),
    ]);
    setCanales(c);
    setVideos(v);
    setAudios(a);
    setCargando(false);
  }

  function abrirNuevo() {
    const datos = tab === "canales" ? { ...CANAL_VACIO } : tab === "videos" ? { ...VIDEO_VACIO } : { ...AUDIO_VACIO };
    setModal({ tipo: tab, datos, esNuevo: true });
  }

  function abrirEditar(item) {
    setModal({ tipo: tab, datos: { ...item }, esNuevo: false });
  }

  async function guardar() {
    setGuardando(true);
    const { tipo, datos, esNuevo } = modal;
    if (tipo === "canales") {
      if (esNuevo) await Canal.create(datos); else await Canal.update(datos.id, datos);
    } else if (tipo === "videos") {
      if (esNuevo) await Video.create(datos); else await Video.update(datos.id, datos);
    } else {
      if (esNuevo) await Audio.create(datos); else await Audio.update(datos.id, datos);
    }
    setModal(null);
    setGuardando(false);
    await cargarTodo();
  }

  async function eliminar(id) {
    if (tab === "canales") await Canal.delete(id);
    else if (tab === "videos") await Video.delete(id);
    else await Audio.delete(id);
    setConfirmEliminar(null);
    await cargarTodo();
  }

  function setField(campo, valor) {
    setModal((m) => ({ ...m, datos: { ...m.datos, [campo]: valor } }));
  }

  const items = tab === "canales" ? canales : tab === "videos" ? videos : audios;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-primary text-primary-foreground px-6 py-4 flex items-center gap-3">
        <Settings size={24} />
        <div>
          <h1 className="text-xl font-black">Administración</h1>
          <p className="text-xs text-white/70">Gestiona canales, videos y audios</p>
        </div>
        <button
          onClick={cargarTodo}
          className="ml-auto text-white/70 hover:text-white transition-colors"
        >
          <RefreshCw size={18} className={cargando ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border bg-secondary">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-all border-b-2
              ${tab === id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>

      {/* Contenido */}
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <p className="text-sm text-muted-foreground">{items.length} registros</p>
          <button
            onClick={abrirNuevo}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded text-sm font-semibold hover:bg-primary/80 transition-all"
          >
            <Plus size={16} />
            Agregar nuevo
          </button>
        </div>

        {cargando ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground">
            <RefreshCw size={24} className="animate-spin mr-2" />
            Cargando...
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <p>No hay registros. Agrega el primero.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-secondary-foreground">
                <tr>
                  {tab === "canales" && <>
                    <th className="text-left px-4 py-3">Nombre</th>
                    <th className="text-left px-4 py-3">Categoría</th>
                    <th className="text-left px-4 py-3">Estado</th>
                    <th className="text-left px-4 py-3">Orden</th>
                  </>}
                  {tab === "videos" && <>
                    <th className="text-left px-4 py-3">Título</th>
                    <th className="text-left px-4 py-3">Artista</th>
                    <th className="text-left px-4 py-3">Categoría</th>
                    <th className="text-left px-4 py-3">Estado</th>
                  </>}
                  {tab === "audios" && <>
                    <th className="text-left px-4 py-3">Título</th>
                    <th className="text-left px-4 py-3">Artista</th>
                    <th className="text-left px-4 py-3">Categoría</th>
                    <th className="text-left px-4 py-3">Estado</th>
                  </>}
                  <th className="text-right px-4 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, i) => (
                  <tr key={item.id} className={`border-t border-border ${i % 2 === 0 ? "bg-background" : "bg-secondary/30"}`}>
                    {tab === "canales" && <>
                      <td className="px-4 py-3 font-medium">{item.nombre}</td>
                      <td className="px-4 py-3 text-muted-foreground">{item.categoria}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${item.activo ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                          {item.activo ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{item.orden}</td>
                    </>}
                    {tab === "videos" && <>
                      <td className="px-4 py-3 font-medium">{item.titulo}</td>
                      <td className="px-4 py-3 text-muted-foreground">{item.artista || "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{item.categoria}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${item.activo ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                          {item.activo ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                    </>}
                    {tab === "audios" && <>
                      <td className="px-4 py-3 font-medium">{item.titulo}</td>
                      <td className="px-4 py-3 text-muted-foreground">{item.artista || "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{item.categoria}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${item.activo ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                          {item.activo ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                    </>}
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => abrirEditar(item)} className="text-primary hover:text-primary/70 transition-colors">
                          <Pencil size={16} />
                        </button>
                        <button onClick={() => setConfirmEliminar(item.id)} className="text-destructive hover:text-destructive/70 transition-colors">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal edición/creación */}
      {modal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h2 className="font-black text-lg">
                {modal.esNuevo ? "Agregar" : "Editar"}{" "}
                {modal.tipo === "canales" ? "Canal" : modal.tipo === "videos" ? "Video" : "Audio"}
              </h2>
              <button onClick={() => setModal(null)} className="text-muted-foreground hover:text-foreground">
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Campos Canal */}
              {modal.tipo === "canales" && <>
                <Campo label="Nombre del canal *" value={modal.datos.nombre} onChange={(v) => setField("nombre", v)} />
                <Campo label="URL del stream *" value={modal.datos.url_stream} onChange={(v) => setField("url_stream", v)} placeholder="https://youtube.com/watch?v=..." />
                <Campo label="Descripción" value={modal.datos.descripcion} onChange={(v) => setField("descripcion", v)} multiline />
                <SelectCampo label="Categoría" value={modal.datos.categoria} options={CANAL_CATS} onChange={(v) => setField("categoria", v)} />
                <Campo label="URL de imagen/logo" value={modal.datos.imagen} onChange={(v) => setField("imagen", v)} />
                <Campo label="Orden" value={modal.datos.orden} onChange={(v) => setField("orden", parseInt(v) || 0)} type="number" />
                <ToggleCampo label="Canal activo" value={modal.datos.activo} onChange={(v) => setField("activo", v)} />
              </>}
              {/* Campos Video */}
              {modal.tipo === "videos" && <>
                <Campo label="Título *" value={modal.datos.titulo} onChange={(v) => setField("titulo", v)} />
                <Campo label="URL del video *" value={modal.datos.url_video} onChange={(v) => setField("url_video", v)} placeholder="https://youtube.com/watch?v=..." />
                <Campo label="Artista" value={modal.datos.artista} onChange={(v) => setField("artista", v)} />
                <Campo label="Descripción" value={modal.datos.descripcion} onChange={(v) => setField("descripcion", v)} multiline />
                <SelectCampo label="Categoría" value={modal.datos.categoria} options={VIDEO_CATS} onChange={(v) => setField("categoria", v)} />
                <Campo label="URL de miniatura" value={modal.datos.miniatura} onChange={(v) => setField("miniatura", v)} />
                <Campo label="Duración (ej: 3:45)" value={modal.datos.duracion} onChange={(v) => setField("duracion", v)} />
                <Campo label="Orden" value={modal.datos.orden} onChange={(v) => setField("orden", parseInt(v) || 0)} type="number" />
                <ToggleCampo label="Video activo" value={modal.datos.activo} onChange={(v) => setField("activo", v)} />
              </>}
              {/* Campos Audio */}
              {modal.tipo === "audios" && <>
                <Campo label="Título *" value={modal.datos.titulo} onChange={(v) => setField("titulo", v)} />
                <Campo label="URL del audio (MP3) *" value={modal.datos.url_audio} onChange={(v) => setField("url_audio", v)} placeholder="https://ejemplo.com/cancion.mp3" />
                <Campo label="Artista" value={modal.datos.artista} onChange={(v) => setField("artista", v)} />
                <Campo label="Álbum" value={modal.datos.album} onChange={(v) => setField("album", v)} />
                <Campo label="Descripción" value={modal.datos.descripcion} onChange={(v) => setField("descripcion", v)} multiline />
                <SelectCampo label="Categoría" value={modal.datos.categoria} options={AUDIO_CATS} onChange={(v) => setField("categoria", v)} />
                <Campo label="URL de portada" value={modal.datos.portada} onChange={(v) => setField("portada", v)} />
                <Campo label="Duración (ej: 3:45)" value={modal.datos.duracion} onChange={(v) => setField("duracion", v)} />
                <Campo label="Orden" value={modal.datos.orden} onChange={(v) => setField("orden", parseInt(v) || 0)} type="number" />
                <ToggleCampo label="Audio activo" value={modal.datos.activo} onChange={(v) => setField("activo", v)} />
              </>}
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-border">
              <button onClick={() => setModal(null)} className="px-4 py-2 rounded border border-border text-sm font-semibold hover:bg-muted transition-all">
                Cancelar
              </button>
              <button
                onClick={guardar}
                disabled={guardando}
                className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded text-sm font-semibold hover:bg-primary/80 transition-all disabled:opacity-60"
              >
                {guardando ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                {guardando ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmar eliminar */}
      {confirmEliminar && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-xl w-full max-w-sm shadow-2xl p-6">
            <h3 className="font-black text-lg mb-2">¿Eliminar registro?</h3>
            <p className="text-muted-foreground text-sm mb-5">Esta acción no se puede deshacer.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmEliminar(null)} className="px-4 py-2 rounded border border-border text-sm font-semibold hover:bg-muted">
                Cancelar
              </button>
              <button onClick={() => eliminar(confirmEliminar)} className="px-4 py-2 rounded bg-destructive text-destructive-foreground text-sm font-semibold hover:bg-destructive/80">
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Campo({ label, value, onChange, placeholder, multiline, type = "text" }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-foreground mb-1">{label}</label>
      {multiline ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          className="w-full bg-background border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-primary resize-none"
        />
      ) : (
        <input
          type={type}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-background border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-primary"
        />
      )}
    </div>
  );
}

function SelectCampo({ label, value, options, onChange }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-foreground mb-1">{label}</label>
      <div className="relative">
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-background border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-primary appearance-none"
        >
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
      </div>
    </div>
  );
}

function ToggleCampo({ label, value, onChange }) {
  return (
    <div className="flex items-center justify-between">
      <label className="text-sm font-semibold text-foreground">{label}</label>
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={`relative w-11 h-6 rounded-full transition-colors ${value ? "bg-primary" : "bg-muted"}`}
      >
        <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${value ? "translate-x-6" : "translate-x-1"}`} />
      </button>
    </div>
  );
}